import { useCallback, useState } from "react";
import { useGlobalContext } from "../../Context/GlobalContext";

const saveProfileInStorage = (profile) => {
  if (!profile) {
    return;
  }

  try {
    const existingCustomer = sessionStorage.getItem("customer");

    let previousCustomer = {};

    if (existingCustomer) {
      try {
        previousCustomer = JSON.parse(existingCustomer) || {};
      } catch {
        previousCustomer = {};
      }
    }

    const updatedCustomer = {
      ...previousCustomer,
      ...profile,

      profileImage:
        profile?.profileImage ||
        profile?.profile_image ||
        previousCustomer?.profileImage ||
        previousCustomer?.profile_image ||
        null,

      profile_image:
        profile?.profile_image ||
        profile?.profileImage ||
        previousCustomer?.profile_image ||
        previousCustomer?.profileImage ||
        null,
    };

    const duplicateCustomerKeys = [
      "chaafoUser",
      "chaafo_user",
      "user_code",
      "first_name",
      "last_name",
      "username",
      "email",
      "phone",
      "number",
      "date_of_birth",
      "gender",
      "age",
      "privacy_policy",
      "profile_image",
    ];

    duplicateCustomerKeys.forEach((key) => {
      sessionStorage.removeItem(key);
    });

    sessionStorage.setItem("customer", JSON.stringify(updatedCustomer));
  } catch (error) {
    console.error("Unable to save profile in storage:", error);
  }
};

const getStoredProfile = () => {
  try {
    const storedCustomer = sessionStorage.getItem("customer");

    if (!storedCustomer) {
      return null;
    }

    const parsedCustomer = JSON.parse(storedCustomer);

    if (
      parsedCustomer &&
      typeof parsedCustomer === "object" &&
      !Array.isArray(parsedCustomer)
    ) {
      return parsedCustomer;
    }

    return null;
  } catch (error) {
    console.error("Unable to read profile from session storage:", error);
    return null;
  }
};

export const useProfile = () => {
  const { api, anlookImageApi } = useGlobalContext();

  const [profileDetails, setProfileDetails] = useState(() => {
    return getStoredProfile();
  });

  const [profileLoading, setProfileLoading] = useState(false);

  const [profileError, setProfileError] = useState("");

  // =====================================================
  // GET PROFILE DETAILS
  // =====================================================

  const getProfileDetails = useCallback(
    async ({ userCode }) => {
      const cleanUserCode = String(userCode || "").trim();

      if (!cleanUserCode) {
        const message = "User code is required.";

        setProfileDetails(null);
        setProfileError(message);

        return {
          success: false,
          data: null,
          message,
        };
      }

      if (!api) {
        const message = "API instance is not available.";

        setProfileDetails(null);
        setProfileError(message);

        return {
          success: false,
          data: null,
          message,
        };
      }

      try {
        setProfileLoading(true);
        setProfileError("");

        const payload = {
          user_code: cleanUserCode,
        };

        console.log("getProfileDetails payload:", payload);

        const response = await api.post("/Chaafo/getProfileDetails", payload, {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        });

        const responseData = response?.data;

        console.log("getProfileDetails response:", responseData);

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage || "Unable to fetch profile details.",
          );
        }

        const responseList = Array.isArray(responseData?.response)
          ? responseData.response
          : [];

        const profile = responseList.length > 0 ? responseList[0] : null;

        setProfileDetails(profile);

        if (profile) {
          saveProfileInStorage(profile);
        }

        return {
          success: true,
          data: profile,
          message: responseData?.responseMessage || "",
        };
      } catch (error) {
        console.error(
          "Get profile details error:",
          error?.response?.data || error,
        );

        const responseData = error?.response?.data;

        const message =
          typeof responseData === "object"
            ? responseData?.responseMessage ||
              responseData?.message ||
              "Unable to fetch profile details."
            : error?.message || "Unable to fetch profile details.";

        setProfileDetails(null);
        setProfileError(message);

        return {
          success: false,
          data: null,
          message,
        };
      } finally {
        setProfileLoading(false);
      }
    },
    [api],
  );

  // =====================================================
  // INSERT PROFILE DETAILS
  // =====================================================

  const insertProfileDetails = useCallback(
    async ({ userCode, firstName, lastName, privacyPolicy = "Y" }) => {
      const cleanUserCode = String(userCode || "").trim();

      const cleanFirstName = String(firstName || "").trim();

      const cleanLastName = String(lastName || "").trim();

      const cleanPrivacyPolicy =
        String(privacyPolicy || "N").toUpperCase() === "Y" ? "Y" : "N";

      if (!api) {
        throw new Error("API instance is not available.");
      }

      if (!cleanUserCode) {
        throw new Error("User code is required.");
      }

      if (!cleanFirstName) {
        throw new Error("First name is required.");
      }

      if (!cleanLastName) {
        throw new Error("Last name is required.");
      }

      if (cleanPrivacyPolicy !== "Y") {
        throw new Error("Please accept the Privacy Policy.");
      }

      try {
        setProfileLoading(true);
        setProfileError("");

        const payload = {
          user_code: cleanUserCode,
          first_name: cleanFirstName,
          last_name: cleanLastName,
          privacy_policy: cleanPrivacyPolicy,
        };

        const response = await api.post(
          "/Chaafo/insertProfileDetails",
          payload,
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        const responseData = response?.data;

        if (
          responseData?.responseCode &&
          String(responseData.responseCode) !== "00"
        ) {
          throw new Error(
            responseData?.responseMessage || "Unable to save profile details.",
          );
        }

        const profile = {
          user_code: cleanUserCode,
          first_name: cleanFirstName,
          last_name: cleanLastName,
          privacy_policy: cleanPrivacyPolicy,
        };

        setProfileDetails((previous) => ({
          ...(previous || {}),
          ...profile,
        }));

        saveProfileInStorage({
          ...(profileDetails || {}),
          ...profile,
        });

        return responseData;
      } catch (error) {
        console.error(
          "Insert profile details error:",
          error?.response?.data || error,
        );

        const responseData = error?.response?.data;

        const message =
          typeof responseData === "object"
            ? responseData?.responseMessage ||
              responseData?.message ||
              "Unable to save profile details."
            : error?.message || "Unable to save profile details.";

        setProfileError(message);

        throw new Error(message);
      } finally {
        setProfileLoading(false);
      }
    },
    [api, profileDetails],
  );

  // =====================================================
  // UPLOAD PROFILE IMAGE USING PHP API
  // =====================================================

  const uploadProfileImage = useCallback(
    async ({ userCode, imageData, fileName = "profile_image" }) => {
      const cleanUserCode = String(userCode || "").trim();

      const cleanImageData = String(imageData || "").trim();

      const cleanFileName = String(fileName || "profile_image").trim();

      if (!anlookImageApi) {
        return {
          success: false,
          imageUrl: "",
          message: "Image upload API is not available.",
        };
      }

      if (!cleanUserCode) {
        return {
          success: false,
          imageUrl: "",
          message: "User code is missing.",
        };
      }

      if (!cleanImageData) {
        return {
          success: false,
          imageUrl: "",
          message: "Image is missing.",
        };
      }

      try {
        const payload = {
          userid: cleanUserCode,

          item_name: cleanFileName,

          type: "IMG",

          image_data: cleanImageData,
        };

        console.log("uploadProfileImage payload:", {
          userid: payload.userid,
          item_name: payload.item_name,
          type: payload.type,
          image_data: payload.image_data ? "BASE64_IMAGE" : "",
        });

        const response = await anlookImageApi.post(
          "/v1/uploadItemImagev2.php",
          payload,
        );

        const responseData = response?.data;

        console.log("uploadProfileImage response:", responseData);

        if (String(responseData?.status) === "0" && responseData?.image_url) {
          return {
            success: true,

            imageUrl: String(responseData.image_url).trim(),

            data: responseData,

            message:
              responseData?.message || "Profile image uploaded successfully.",
          };
        }

        return {
          success: false,

          imageUrl: "",

          data: responseData,

          message:
            responseData?.message ||
            responseData?.responseMessage ||
            "Unable to upload profile image.",
        };
      } catch (error) {
        console.error(
          "Upload profile image error:",
          error?.response?.data || error,
        );

        return {
          success: false,

          imageUrl: "",

          data: error?.response?.data || null,

          message:
            error?.response?.data?.message ||
            error?.response?.data?.responseMessage ||
            error?.message ||
            "Unable to upload profile image.",
        };
      }
    },
    [anlookImageApi],
  );

  const updateProfileDetails = useCallback(
    async ({
      userCode,

      firstName,
      lastName,

      username,
      email,
      dateOfBirth,
      gender,
      age,
      profileImage,
    }) => {
      const cleanUserCode = String(userCode || "").trim();

      const cleanFirstName = String(firstName || "").trim();
      const cleanLastName = String(lastName || "").trim();

      const cleanUsername = String(username || "").trim();
      const cleanEmail = String(email || "").trim();
      const cleanDateOfBirth = String(dateOfBirth || "").trim();
      const cleanGender = String(gender || "").trim();
      const cleanAge = String(age || "").trim();
      const cleanProfileImage = String(profileImage || "").trim();

      if (!api) {
        throw new Error("API instance is not available.");
      }

      if (!cleanUserCode) {
        throw new Error("User code is required.");
      }

      if (!cleanUsername) {
        throw new Error("Username is required.");
      }

      if (!cleanEmail) {
        throw new Error("Email is required.");
      }

      try {
        setProfileLoading(true);
        setProfileError("");

        // =====================================================
        // API PAYLOAD
        // Backend ka required payload same rakha hai
        // =====================================================

        const payload = {
          user_code: cleanUserCode,
          email: cleanEmail,
          username: cleanUsername,
          date_of_birth: cleanDateOfBirth,
          gender: cleanGender,
          age: cleanAge,
          profileImage: cleanProfileImage,
        };

        console.log("updateProfileDetails payload:", payload);

        const response = await api.post(
          "/Chaafo/updateProfileDetails",
          payload,
          {
            headers: {
              accept: "*/*",
              "Content-Type": "application/json-patch+json",
            },
          },
        );

        const responseData = response?.data;

        console.log("updateProfileDetails response:", responseData);

        if (String(responseData?.responseCode) !== "00") {
          throw new Error(
            responseData?.responseMessage || "Unable to update profile.",
          );
        }

        // =====================================================
        // UPDATE COMPLETE LOCAL PROFILE
        // Yahan modal ke saare editable fields update honge
        // =====================================================

        const updatedProfile = {
          ...(profileDetails || {}),

          user_code: cleanUserCode,

          first_name: cleanFirstName,
          last_name: cleanLastName,

          username: cleanUsername,
          email: cleanEmail,

          date_of_birth: cleanDateOfBirth,

          gender: cleanGender,
          age: cleanAge,

          profileImage: cleanProfileImage,
          profile_image: cleanProfileImage,
        };

        // Profile.jsx ke useEffect ko updated data milega
        setProfileDetails(updatedProfile);

        // Session storage bhi updated rahega
        saveProfileInStorage(updatedProfile);

        return {
          success: true,
          data: updatedProfile,
          response: responseData,
        };
      } catch (error) {
        console.error("Update profile error:", error?.response?.data || error);

        const responseData = error?.response?.data;

        const message =
          typeof responseData === "object"
            ? responseData?.responseMessage ||
              responseData?.message ||
              "Unable to update profile."
            : error?.message || "Unable to update profile.";

        setProfileError(message);

        throw new Error(message);
      } finally {
        setProfileLoading(false);
      }
    },
    [api, profileDetails],
  );

  const resetProfile = useCallback(() => {
    setProfileDetails(null);
    setProfileError("");
  }, []);

return {
  profileDetails,

  getProfileDetails,
  insertProfileDetails,
  updateProfileDetails,

  uploadProfileImage,

  profileLoading,
  profileError,

  setProfileError,
  resetProfile,
};
};;
